import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { Switch } from "@/components/ui/switch";
import UserSelect from "@/components/ui/user-select";
import { errorHandler } from "@/lib/utils";
import {
  ReferencePointInput,
  referencePointService,
} from "@/services/api/referencePoints";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import AreaDrawingMap from "../../../components/Map/AreaDrawingMap";
import ReturnRuleSection from "./ReturnRuleSection";

const formSchema = z.object({
  title: z.string().min(1, "O título é obrigatório"),
  users: z.array(z.number()).optional(),
  is_headquarters: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const REF_POINT_DIALOG_PREF_KEY = "dontShowRefPointDialog";

const ReferencePointForm = () => {
  const { id } = useParams<{
    id?: string;
  }>();
  const userId = localStorage.getItem("verden-user-id");
  const clientId = localStorage.getItem("verden-client-id");
  const isAdmin = userId === clientId;

  const [searchParams] = useSearchParams();
  const lat = searchParams.get("lat");
  const lng = searchParams.get("lng");

  const navigate = useNavigate();
  const [polygons, setPolygons] = useState<number[][][]>([]);
  const [circles, setCircles] = useState<
    { center: [number, number]; radius: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const initialValuesRef = useRef({
    title: "",
    users: [] as number[],
    circles: [] as { center: [number, number]; radius: number }[],
    polygons: [] as number[][][],
    is_headquarters: false,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      users: [],
      is_headquarters: false,
    },
  });

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      referencePointService
        .show(Number(id))
        .then((response) => {
          const data = response.data;
          form.setValue("title", data.name);
          form.setValue("is_headquarters", data.is_headquarters || false);

          if (data.polygon_coordinates) {
            const parsedPolygons = data.polygon_coordinates
              .split("|")
              .map((polygon) =>
                polygon.split(";").map((coord) => coord.split(",").map(Number)),
              );
            setPolygons(parsedPolygons);
          }

          if (data.center_latitude && data.center_longitude && data.radius) {
            const newCircles = [
              {
                center: [
                  parseFloat(data.center_latitude),
                  parseFloat(data.center_longitude),
                ] as [number, number],
                radius: data.radius,
              },
            ];
            setCircles(newCircles);
          }

          if (data.users) {
            form.setValue("users", data.users);
          }

          initialValuesRef.current = {
            title: data.name,
            users: data.users || [],
            circles: circles,
            polygons: polygons,
            is_headquarters: data.is_headquarters || false,
          };
        })
        .catch((err) => {
          errorHandler(err, "Erro ao carregar ponto de referência");
        })
        .finally(() => setIsLoading(false));
    } else if (lat && lng) {
      const initialCircle = {
        center: [parseFloat(lat), parseFloat(lng)] as [number, number],
        radius: 100,
      };
      setCircles([initialCircle]);
      form.setValue("title", "");
    }
  }, [id, form, lat, lng]);

  useEffect(() => {
    if (!id) return;

    const currentValues = form.getValues();
    const initialValues = initialValuesRef.current;

    const formValuesChanged =
      currentValues.title !== initialValues.title ||
      JSON.stringify(currentValues.users) !==
        JSON.stringify(initialValues.users) ||
      currentValues.is_headquarters !== initialValues.is_headquarters;

    let circlesChanged = false;
    if (circles.length !== initialValues.circles.length) {
      circlesChanged = true;
    } else if (circles.length > 0 && initialValues.circles.length > 0) {
      const currentCircle = circles[0];
      const initialCircle = initialValues.circles[0];

      circlesChanged =
        currentCircle.center[0] !== initialCircle.center[0] ||
        currentCircle.center[1] !== initialCircle.center[1] ||
        currentCircle.radius !== initialCircle.radius;
    }

    const polygonsChanged =
      JSON.stringify(polygons) !== JSON.stringify(initialValues.polygons);

    setHasChanges(formValuesChanged || circlesChanged || polygonsChanged);
  }, [form.watch(), circles, polygons, id]);

  const onSubmit = async (data: FormValues) => {
    if (circles.length === 0 && polygons.length === 0) {
      toast.error("Por favor, desenhe o ponto de referência no mapa.");
      return;
    }

    if (id && hasChanges) {
      const dontShowDialog =
        localStorage.getItem(REF_POINT_DIALOG_PREF_KEY) === "true";

      if (dontShowDialog) {
        await saveForm(data);
      } else {
        setShowConfirmDialog(true);
      }
      return;
    }

    await saveForm(data);
  };

  const saveForm = async (data: FormValues) => {
    try {
      const payload: ReferencePointInput = {
        name: data.title,
        radius: circles.length > 0 ? circles[0].radius : 0,
        center_latitude:
          circles.length > 0 ? circles[0].center[0].toString() : null,
        center_longitude:
          circles.length > 0 ? circles[0].center[1].toString() : null,
        polygon_coordinates:
          polygons.length > 0
            ? polygons[0].map((coord) => `${coord[0]},${coord[1]}`).join(";")
            : null,
        is_headquarters: data.is_headquarters || false,
        users: data.users,
      };

      if (id) {
        await referencePointService.update(Number(id), payload);
        form.reset();
        toast.success("Ponto de referência atualizado com sucesso!");
      } else {
        await referencePointService.create(payload);
        toast.success("Ponto de referência criado com sucesso!");
      }

      navigate("/reference-points");
    } catch (err) {
      errorHandler(err, "Erro ao salvar ponto de referência");
    }
  };

  const handleConfirmSave = () => {
    if (dontShowAgain) {
      localStorage.setItem(REF_POINT_DIALOG_PREF_KEY, "true");
    }
    saveForm(form.getValues());
  };

  return (
    <>
      <Card className="mx-auto">
        <CardHeader>
          <CardTitle>
            {id
              ? "Editar Ponto de Referência"
              : "Cadastrar Ponto de Referência"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="flex flex-col md:grid md:grid-cols-4 gap-3 h-full w-full">
                <div className="w-full md:col-span-1 p-4 flex flex-col gap-3">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Título</FormLabel>
                        <FormControl>
                          <Input placeholder="Digite o título" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {isAdmin && (
                    <FormField
                      control={form.control}
                      name="users"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Usuários com permissão</FormLabel>
                          <FormControl>
                            <UserSelect
                              value={field.value}
                              onChange={(value) => field.onChange(value)}
                              multiple
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="is_headquarters"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div
                          className="space-y-0.5 hover:cursor-pointer"
                          onClick={() => field.onChange(!field.value)}
                        >
                          <FormLabel
                            className="hover:cursor-pointer"
                            htmlFor="is_headquarters"
                          ></FormLabel>
                          <FormDescription>
                            Marque esta opção se este ponto representa uma sede
                            da empresa. Pontos de sede não são contabilizados no
                            relatório de visitas.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            className="bg-primary-500"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-2 space-y-2 pt-8 xl:flex-row xl:space-x-2 xl:space-y-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/reference-points")}
                    >
                      Cancelar
                    </Button>
                    <SubmitButton loading={isLoading} />
                  </div>
                </div>
                <div className="w-full h-[400px] md:h-[600px] lg:h-[600px] md:col-span-3 pt-3 md:pt-0">
                  <AreaDrawingMap
                    onPolygonsChange={setPolygons}
                    onCirclesChange={setCircles}
                    initialPolygons={polygons}
                    initialCircles={circles}
                    toLoad="reference_points"
                    editingAreaId={id ? Number(id) : undefined}
                  />
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {id && !isLoading && (
        <div className="mt-6">
          <ReturnRuleSection
            referencePointId={Number(id)}
            hasGeometry={circles.length > 0 || polygons.length > 0}
          />
        </div>
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
            <AlertDialogDescription>
              Você fez alterações neste ponto de referência. Por favor, confirme
              se todos os dados estão corretos antes de salvar.
              <br />
              <br /> Lembre-se que sempre que você fizer mudanças no círculo ou
              polígono no mapa, deve pressionar o botão salvar que fica ao lado
              das ferramentas de desenho para confirmar as alterações na área.
              Verifique se a área está posicionada corretamente antes de
              continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center space-x-2 py-4 px-6">
            <Checkbox
              id="dontShowAgain"
              checked={dontShowAgain}
              onCheckedChange={(checked) =>
                setDontShowAgain(checked as boolean)
              }
            />
            <label
              htmlFor="dontShowAgain"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Não exibir mais este aviso
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Revisar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>
              Confirmar e Salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ReferencePointForm;
